import { type QueryClient } from '@tanstack/react-query';

import { PRIVATE_WORKSPACE_AGENT_CLIPS_LIST_BY_IDS_KEY } from '~/components/v2/components/workspace/workspace-agent-panel/queries/usePrivateWorkspaceAgentClipsListByIds';
import { PRIVATE_WORKSPACE_AGENT_PILL_CLIPS_KEY } from '~/components/v2/components/workspace/workspace-agent-panel/queries/usePrivateWorkspaceAgentPillClips';
import { type WorkspaceAgentCurrentViewInvalidationTargets } from '~/components/v2/components/workspace/workspace-agent-panel/utils/getWorkspaceAgentCurrentViewInvalidationTargets';
import { isWorkspaceAgentBoardNavigationQueryKey } from '~/components/v2/components/workspace/workspace-agent-panel/utils/invalidateWorkspaceAgentBoardNavigationQueries';
import { isPrivateBoardKey } from '~/components/v2/hooks/queries/usePrivateBoard';
import { isPrivateBoardStatsKey } from '~/components/v2/hooks/queries/usePrivateBoardsStats';
import {
  ASSETS_CUSTOM_FIELDS,
  GALLERY_ASSETS,
  GALLERY_BOARDS,
  GALLERY_FILES,
  GALLERY_MIXED_DATA,
  getKanbanColumnItemsKey,
  getVersionsListKey,
} from '~/constants/react-query-keys';
import { CLIP_KEY } from '~/swr-hooks/clips/useClip';
import { PRIVATE_CLIP_METADATA_KEY } from '~/swr-hooks/clips/usePrivateClipMetadata';
import { isClipWithoutWorkspaceIdKey } from '~/swr-hooks/useGetClipWithNoWorkspaceId';

const GALLERY_MAIN_KEYS = new Set([GALLERY_ASSETS, GALLERY_BOARDS, GALLERY_FILES, GALLERY_MIXED_DATA]);
const VERSIONS_MAIN_KEY = getVersionsListKey({ assetId: '' })[0];
const KANBAN_COLUMN_ITEMS_MAIN_KEY = getKanbanColumnItemsKey({ kanbanColumnId: '' })[0];

const hasClipId = (keyParams: unknown, clipIdSet: Set<string>): boolean =>
  typeof keyParams === 'object' &&
  keyParams !== null &&
  'clipId' in keyParams &&
  typeof keyParams.clipId === 'string' &&
  clipIdSet.has(keyParams.clipId);

const hasAssetId = (keyParams: unknown, assetIdSet: Set<string>): boolean =>
  typeof keyParams === 'object' &&
  keyParams !== null &&
  'assetId' in keyParams &&
  typeof keyParams.assetId === 'string' &&
  assetIdSet.has(keyParams.assetId);

const hasBoardId = (keyParams: unknown, boardIdSet: Set<string>): boolean =>
  typeof keyParams === 'object' &&
  keyParams !== null &&
  'boardId' in keyParams &&
  typeof keyParams.boardId === 'string' &&
  boardIdSet.has(keyParams.boardId);

const pillClipsQueryMatches = (keyParams: unknown, clipIdSet: Set<string>): boolean => {
  if (typeof keyParams !== 'object' || keyParams === null || !('clipIds' in keyParams)) {
    return false;
  }

  const clipIds = keyParams.clipIds;

  return Array.isArray(clipIds) && clipIds.some((clipId) => typeof clipId === 'string' && clipIdSet.has(clipId));
};

const readAssetIdFromClipCacheData = (data: unknown, clipId: string): string | undefined => {
  if (typeof data !== 'object' || data === null || !('id' in data) || data.id !== clipId) {
    return undefined;
  }

  if (!('assetId' in data) || typeof data.assetId !== 'string') {
    return undefined;
  }

  return data.assetId;
};

/** Resolves asset ids for the open modal clip using clip rows already in the query cache. */
const resolveWorkspaceAgentAssetIdsFromClipCache = (queryClient: QueryClient, clipIds: readonly string[]): string[] => {
  if (clipIds.length === 0) {
    return [];
  }

  const clipIdSet = new Set(clipIds);
  const assetIds = new Set<string>();

  for (const query of queryClient.getQueryCache().getAll()) {
    if (isClipWithoutWorkspaceIdKey(query.queryKey)) {
      const clipId = query.queryKey[1].clipId;

      if (clipIdSet.has(clipId)) {
        const assetId = readAssetIdFromClipCacheData(query.state.data, clipId);

        if (assetId) {
          assetIds.add(assetId);
        }
      }

      continue;
    }

    const [mainKey, keyParams] = query.queryKey;

    if (mainKey === CLIP_KEY && typeof keyParams === 'object' && keyParams !== null && 'clipId' in keyParams) {
      const clipId = keyParams.clipId;

      if (typeof clipId === 'string' && clipIdSet.has(clipId)) {
        const assetId = readAssetIdFromClipCacheData(query.state.data, clipId);

        if (assetId) {
          assetIds.add(assetId);
        }
      }
    }
  }

  return [...assetIds];
};

export type InvalidateWorkspaceAgentModifiedAssetQueriesParams = {
  currentView: WorkspaceAgentCurrentViewInvalidationTargets;
  queryClient: QueryClient;
};

export const invalidateWorkspaceAgentModifiedAssetQueries = ({
  currentView,
  queryClient,
}: InvalidateWorkspaceAgentModifiedAssetQueriesParams): void => {
  const clipIds = currentView.clipId ? [currentView.clipId] : [];
  const boardIds = currentView.boardId ? [currentView.boardId] : [];
  const clipIdSet = new Set(clipIds);
  const boardIdSet = new Set(boardIds);
  const assetIdSet = new Set([
    ...(currentView.assetId ? [currentView.assetId] : []),
    ...resolveWorkspaceAgentAssetIdsFromClipCache(queryClient, clipIds),
  ]);

  void queryClient.invalidateQueries({
    predicate: ({ queryKey }) => {
      if (isClipWithoutWorkspaceIdKey(queryKey)) {
        return hasClipId(queryKey[1], clipIdSet);
      }

      if (isPrivateBoardKey(queryKey) || isPrivateBoardStatsKey(queryKey)) {
        return hasBoardId(queryKey[1], boardIdSet);
      }

      if (isWorkspaceAgentBoardNavigationQueryKey(queryKey)) {
        return true;
      }

      const [mainKey, keyParams] = queryKey;

      if (typeof mainKey !== 'string') {
        return false;
      }

      if (mainKey === CLIP_KEY || mainKey === PRIVATE_CLIP_METADATA_KEY) {
        return hasClipId(keyParams, clipIdSet);
      }

      if (mainKey === VERSIONS_MAIN_KEY || mainKey === ASSETS_CUSTOM_FIELDS) {
        return hasAssetId(keyParams, assetIdSet);
      }

      if (
        mainKey === PRIVATE_WORKSPACE_AGENT_PILL_CLIPS_KEY ||
        mainKey === PRIVATE_WORKSPACE_AGENT_CLIPS_LIST_BY_IDS_KEY
      ) {
        return pillClipsQueryMatches(keyParams, clipIdSet);
      }

      if (GALLERY_MAIN_KEYS.has(mainKey)) {
        return true;
      }

      if (currentView.isKanbanView && mainKey === KANBAN_COLUMN_ITEMS_MAIN_KEY) {
        return hasBoardId(keyParams, boardIdSet);
      }

      return false;
    },
  });
};
